import {
  PropsWithChildren,
  useMemo,
  useContext,
  useState,
  useEffect,
  RefObject,
} from 'react'
import { loadStripe, StripeError } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, FormikProps } from 'formik'
import isFunction from 'lodash/isFunction'

import './styles.css'
import { STRIPE_PK } from 'app/consts'

import { ToggleField } from 'components/Fields'
import elementsLocaleMapper from 'app/utils/elementsLocaleMapper'
import { LocalizationContext } from 'components/i18n/LocalizationWrapper/LocalizationWrapper'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { CommonButton } from 'components/Button'
import {
  StandalonePaymentMethodSubmit,
  StyledFailedScreen,
  StyledFailedScreenError,
  StyledMakeDefaultToggle,
  StyledMakeDefaultText,
  StyledAddPaymentMethodForm,
  StyledChargesInfo,
} from './styles'
import { paymentMethodsMessages } from './messages'
import { LoaderOverlay, SpinnerCentered } from 'components/Loader'
import { setupIntentSelector } from '../../selectors'
import { createSetupIntentThunk } from '../../slice'
import { setDefaultPaymentMethodThunk } from 'features/paymentMethods/slice'
import { commonMessages } from 'app/i18n/messages'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'

interface AddPaymentMethodFormProps {
  onCancel?: () => void
  onSuccess: () => void
  isFirstMethod?: boolean
  isStandalone?: boolean
  formRef?: RefObject<FormikProps<AddPaymentMethodDetails>>
}

export interface AddPaymentMethodDetails {
  setDefault: boolean
}

const initialBillingDetails: AddPaymentMethodDetails = {
  setDefault: true,
}

function AddPaymentMethodForm(
  props: PropsWithChildren<AddPaymentMethodFormProps>
) {
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useAppDispatch()
  const rootId = useAppSelector(currentRootDomainIdSelector)
  const { formatMessage } = useIntl()
  const [error, setError] = useState<StripeError | undefined>(undefined)
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)

  useEffect(() => {
    if (elements) {
      const paymentElement = elements.getElement('payment')
      if (paymentElement) {
        paymentElement.on('change', function (event) {
          if (event.complete) {
            setSubmitDisabled(false)
          }
        })
      }
    }
  }, [elements])

  const handleSubmit = async (values: AddPaymentMethodDetails) => {
    setSubmitting(true)

    if (!stripe || !elements) {
      setSubmitting(false)
      return
    }

    const { setDefault } = values

    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    })

    if (result.error) {
      console.log('error')
      setError(result.error)
      setSubmitting(false)
      return
    } else {
      if (rootId) {
        await dispatch(createSetupIntentThunk(rootId))
        if (setDefault && result.setupIntent.payment_method) {
          await dispatch(
            setDefaultPaymentMethodThunk(
              result.setupIntent.payment_method as string,
              rootId
            )
          )
        }
      }
    }

    await props.onSuccess()
    setSubmitting(false)
  }

  if (error) {
    return (
      <StyledFailedScreen>
        <StyledFailedScreenError>
          {error.type === 'card_error' || error.type === 'validation_error'
            ? error.message
            : formatMessage(paymentMethodsMessages.defaultError)}
        </StyledFailedScreenError>
        <CommonButton
          type="button"
          onClick={() => {
            setError(undefined)
          }}
        >
          {formatMessage(paymentMethodsMessages.backToForm)}
        </CommonButton>
      </StyledFailedScreen>
    )
  }

  return (
    <Formik initialValues={initialBillingDetails} onSubmit={handleSubmit}>
      <StyledAddPaymentMethodForm $isStandalone={props.isStandalone}>
        {submitting && <LoaderOverlay />}
        <PaymentElement />
        <StyledMakeDefaultToggle>
          <StyledMakeDefaultText>
            {formatMessage(paymentMethodsMessages.makeDefault)}{' '}
          </StyledMakeDefaultText>
          <ToggleField
            disabled={props.isFirstMethod}
            name="setDefault"
            inline
          />
        </StyledMakeDefaultToggle>

        {isFunction(props.children)
          ? props.children({
              isSubmitDisabled: submitting || submitDisabled,
            })
          : null}

        {props.isStandalone ? (
          <StandalonePaymentMethodSubmit>
            <CommonButton
              variant="primary"
              type="submit"
              disabled={!stripe || submitDisabled}
            >
              <FormattedMessage {...commonMessages.save} />
            </CommonButton>
          </StandalonePaymentMethodSubmit>
        ) : (
          <>
            <StyledChargesInfo>
              {formatMessage(paymentMethodsMessages.chargesInfo)}
            </StyledChargesInfo>
          </>
        )}
      </StyledAddPaymentMethodForm>
    </Formik>
  )
}

export default function InjectedForm(
  props: PropsWithChildren<AddPaymentMethodFormProps>
) {
  const currentSetupIntent = useAppSelector(setupIntentSelector.data)
  const localizationContext = useContext(LocalizationContext)

  const stripePromise = useMemo(
    () =>
      loadStripe(STRIPE_PK || '', {
        locale: elementsLocaleMapper(localizationContext.locale),
      }),
    [localizationContext.locale]
  )

  const options = useMemo(
    () => ({
      clientSecret: currentSetupIntent?.clientSecret,
    }),
    [currentSetupIntent]
  )
  return options.clientSecret ? (
    <Elements stripe={stripePromise} options={options}>
      <AddPaymentMethodForm {...props} />
    </Elements>
  ) : (
    <SpinnerCentered />
  )
}
